// Email Service for Quote Confirmations and PDF Delivery

class QuoteEmailService {
  constructor() {
    this.apiEndpoint = process.env.REACT_APP_API_URL || '/api';
  }

  // Send quote acceptance confirmation to both business owner and client via Amazon SES
  async sendQuoteAcceptanceEmails(quote, acceptanceData, pdfBlob) {
    try {
      console.log('📧 AMAZON SES: Preparing quote acceptance emails...');
      
      const formData = new FormData();
      
      // Add comprehensive quote data for Amazon SES
      const emailData = {
        quoteId: quote.uniqueId,
        clientName: quote.clientName,
        clientEmail: acceptanceData.clientEmail || quote.clientEmail,
        businessName: quote.owner?.businessName || 'Business',
        ownerEmail: quote.owner?.email || 'owner@example.com',
        selectedTier: acceptanceData.selectedTier,
        tierDetails: quote.tiers[acceptanceData.selectedTier],
        totalAmount: (quote.tiers[acceptanceData.selectedTier].price + (quote.interactiveTotal || acceptanceData.interactiveTotal || 0)),
        signatureName: acceptanceData.clientSignatureName,
        signedAt: acceptanceData.signedAt,
        interactiveTotal: quote.interactiveTotal || acceptanceData.interactiveTotal || 0
      };
      
      console.log('📧 Email data prepared:', {
        clientEmail: emailData.clientEmail,
        ownerEmail: emailData.ownerEmail,
        totalAmount: emailData.totalAmount,
        pdfSize: pdfBlob.size
      });
      
      formData.append('quoteData', JSON.stringify(emailData));
      
      // Add PDF attachment with proper naming
      formData.append('quotePDF', pdfBlob, `quote-${quote.uniqueId}-accepted.pdf`);
      
      const response = await fetch(`${this.apiEndpoint}/quotes/send-acceptance-emails`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Email API error:', response.status, errorText);
        throw new Error(`Email service error: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ Amazon SES emails sent:', result);
      
      return { success: true, data: result };
    } catch (error) {
      console.error('❌ Amazon SES email error:', error);
      throw new Error(`Failed to send confirmation emails: ${error.message}`);
    }
  }

  // Send quote viewing notification to business owner
  async sendQuoteViewedNotification(quoteId, viewData) {
    try {
      const response = await fetch(`${this.apiEndpoint}/quotes/viewed-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quoteId,
          viewedAt: viewData.viewedAt,
          userAgent: viewData.userAgent
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to send view notification');
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error sending view notification:', error);
      // Don't throw error - this is optional notification
      return { success: false };
    }
  }

  // Generate email templates
  generateOwnerEmailTemplate(quoteData) {
    return {
      subject: `Quote Accepted: ${quoteData.clientName} - ${this.formatPrice(quoteData.totalAmount)}`,
      html: `
        <div style=\"font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;\">
          <div style=\"background-color: #1e40af; color: white; padding: 20px; text-align: center;\">
            <h1 style=\"margin: 0;\">🎉 Quote Accepted!</h1>
          </div>
          
          <div style=\"padding: 20px; background-color: #f8fafc;\">
            <h2 style=\"color: #1e40af; margin-top: 0;\">Great news! Your quote has been accepted.</h2>
            
            <div style=\"background-color: white; padding: 15px; border-radius: 8px; margin: 15px 0;\">
              <h3 style=\"margin-top: 0; color: #374151;\">Quote Details:</h3>
              <p><strong>Client:</strong> ${quoteData.clientName}</p>
              <p><strong>Email:</strong> ${quoteData.clientEmail}</p>
              <p><strong>Package:</strong> ${quoteData.tierDetails.title}</p>
              <p><strong>Total Amount:</strong> ${this.formatPrice(quoteData.totalAmount)}</p>
              <p><strong>Signed by:</strong> ${quoteData.signatureName}</p>
              <p><strong>Accepted on:</strong> ${this.formatDateTime(quoteData.signedAt)}</p>
            </div>
            
            <div style=\"background-color: #dcfce7; padding: 15px; border-radius: 8px; border-left: 4px solid #16a34a;\">
              <h4 style=\"margin-top: 0; color: #15803d;\">Next Steps:</h4>
              <ul style=\"color: #166534; margin-bottom: 0;\">
                <li>Review the attached signed quote</li>
                <li>Contact ${quoteData.clientName} to schedule the work</li>
                <li>Update your project management system</li>
                <li>Send any additional documentation if required</li>
              </ul>
            </div>
            
            <p style=\"color: #6b7280; font-size: 14px; margin-top: 20px;\">
              The signed quote is attached as a PDF for your records.
            </p>
          </div>
          
          <div style=\"background-color: #374151; color: white; padding: 15px; text-align: center; font-size: 12px;\">
            <p style=\"margin: 0;\">Generated by QuoteTier • Professional Quote Management</p>
          </div>
        </div>
      `
    };
  }

  generateClientEmailTemplate(quoteData) {
    return {
      subject: `Quote Confirmation - ${quoteData.businessName}`,
      html: `
        <div style=\"font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;\">
          <div style=\"background-color: #16a34a; color: white; padding: 20px; text-align: center;\">
            <h1 style=\"margin: 0;\">✅ Quote Confirmed</h1>
          </div>
          
          <div style=\"padding: 20px; background-color: #f8fafc;\">
            <h2 style=\"color: #16a34a; margin-top: 0;\">Thank you for accepting our quote!</h2>
            
            <p>Dear ${quoteData.clientName},</p>
            
            <p>We're excited to confirm that your quote has been successfully accepted. Here are the details:</p>
            
            <div style=\"background-color: white; padding: 15px; border-radius: 8px; margin: 15px 0;\">
              <h3 style=\"margin-top: 0; color: #374151;\">Your Order Summary:</h3>
              <p><strong>Service:</strong> ${quoteData.tierDetails.title}</p>
              <p><strong>Total Amount:</strong> ${this.formatPrice(quoteData.totalAmount)}</p>
              ${quoteData.interactiveTotal > 0 ? `<p><strong>Additional Services:</strong> ${this.formatPrice(quoteData.interactiveTotal)}</p>` : ''}
              <p><strong>Signed on:</strong> ${this.formatDateTime(quoteData.signedAt)}</p>
            </div>
            
            <div style=\"background-color: #dbeafe; padding: 15px; border-radius: 8px; border-left: 4px solid #3b82f6;\">
              <h4 style=\"margin-top: 0; color: #1e40af;\">What happens next?</h4>
              <ul style=\"color: #1e3a8a; margin-bottom: 0;\">
                <li>We'll contact you within 24 hours to schedule the work</li>
                <li>Any questions or changes can be discussed during this call</li>
                <li>You'll receive updates throughout the project</li>
                <li>Payment will be collected as outlined in the agreement</li>
              </ul>
            </div>
            
            <p>If you have any immediate questions, please don't hesitate to contact us.</p>
            
            <p style=\"color: #6b7280; font-size: 14px; margin-top: 20px;\">
              A copy of your signed quote is attached for your records.
            </p>
          </div>
          
          <div style=\"background-color: #374151; color: white; padding: 15px; text-align: center; font-size: 12px;\">
            <p style=\"margin: 0;\">
              ${quoteData.businessName}<br>
              Professional quote powered by QuoteTier
            </p>
          </div>
        </div>
      `
    };
  }

  // Helper methods
  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price || 0);
  }

  formatDateTime(dateString) {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  }

  // Test email configuration (for development)
  async testEmailConfiguration() {
    try {
      const response = await fetch(`${this.apiEndpoint}/email/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Email configuration test failed');
      }
      
      return { success: true, message: 'Email configuration is working' };
    } catch (error) {
      console.error('Email test failed:', error);
      throw error;
    }
  }
}

export const quoteEmailService = new QuoteEmailService();
export default quoteEmailService;